/**
 * Teaching weeks, read from stages: a version holds no week count of its own.
 * @module utils/teaching-weeks
 */

import type { ProgrammeVersion, Stage } from "@/types/version";

/** A version's teaching weeks across its stages, or `undefined` when no stage records any. */
export function versionTeachingWeeks(
  version: Pick<ProgrammeVersion, "stages">,
): number | undefined {
  const weeks = (version.stages ?? [])
    .map((stage) => stage.teachingWeeks)
    .filter((value): value is number => typeof value === "number");
  return weeks.length > 0 ? weeks.reduce((total, value) => total + value, 0) : undefined;
}

/**
 * The weeks a module placed in `placement` runs for: one teaching period's
 * share of its stage when it sits in a single period of several, else the
 * whole stage.
 */
export function moduleTeachingWeeks(
  stage: Pick<Stage, "teachingWeeks" | "semesterCount">,
  placement: string | undefined,
): number | undefined {
  if (stage.teachingWeeks == null) {
    return undefined;
  }
  const isSinglePeriod = /^(?:semester\s*)?\d+$/i.test((placement ?? "").trim());
  const count = Number(stage.semesterCount ?? 0);
  return isSinglePeriod && Number.isInteger(count) && count > 1
    ? stage.teachingWeeks / count
    : stage.teachingWeeks;
}
